import type {
  GateLabels,
  GateTab,
  PassInManualMode,
  Permission,
} from "@nexseed/shared";
import { FORM_ROLES } from "@nexseed/shared";
import { create } from "zustand";
import {
  api,
  onSessionLost,
  refreshSession,
  tokens,
  type SessionLostReason,
} from "@/lib/api-client";

export interface Me {
  id: number;
  email: string;
  fullName: string;
  permissions: Permission[];
  facilityIds: number[] | null;
  /** The form codes this login may view; null when no role narrows it to particular forms. */
  forms: string[] | null;
  /**
   * The form codes this login may create; null when no role narrows it. Optional: an older API
   * leaves it out, and the web then falls back to `forms`.
   */
  formsCreate?: string[] | null | undefined;
  /** The Gate module's names for this login's roles (`roles.gate_labels`); {} = the standard names. */
  gateLabels?: GateLabels | undefined;
  /** The Gate tabs this login's roles show (`roles.gate_tabs`), in tab order. Absent = every tab. */
  gateTabs?: GateTab[] | undefined;
  /** How this login numbers a new Gate Pass In (`roles.pass_in_manual_mode`). Absent = its pick. */
  passInManualMode?: PassInManualMode | undefined;
  /** Optional modules switched on for this server (display only; the API enforces). */
  features?:
    | {
        feedback?: boolean | undefined;
        /** A role of this login opens the handheld: the web tells it when a new app build is out. */
        handheldUser?: boolean | undefined;
      }
    | undefined;
}

interface AuthState {
  me: Me | null;
  status: "unknown" | "authenticated" | "anonymous";
  facilityId: number | null;
  bootstrap: () => Promise<Me | null>;
  /** `force` signs out the account's other web session ("Sign out other system and continue"). */
  login: (email: string, password: string, force?: boolean) => Promise<Me>;
  logout: () => Promise<void>;
  setFacility: (id: number | null) => void;
  can: (p: Permission) => boolean;
  /** The per-form half: false for a form the login's roles do not grant, even with document.view. */
  canViewForm: (formCode: string) => boolean;
  /**
   * May this login start a new document on the form: document.create plus the form's create grant,
   * the rule the API's assertFormAction applies (a FORM_ROLES form needs its own row). Only hides
   * a "New" the API would refuse; the API still decides.
   */
  canCreateForm: (formCode: string) => boolean;
}

const FACILITY_KEY = "nexseed.facility";
const readFacility = () => {
  try {
    const v = localStorage.getItem(FACILITY_KEY);
    return v ? Number(v) : null;
  } catch {
    return null;
  }
};

/** Resolve to `fallback` if `p` has not settled within `ms`; `p` itself keeps running. */
function withTimeout<T>(p: Promise<T>, ms: number, fallback: T): Promise<T> {
  return Promise.race([
    p,
    new Promise<T>((resolve) => setTimeout(() => resolve(fallback), ms)),
  ]);
}

export const useAuth = create<AuthState>((set, get) => ({
  me: null,
  status: "unknown",
  facilityId: readFacility(),
  async bootstrap() {
    if (get().status === "authenticated") return get().me;
    // Capped at 3 s only here, at startup: a hung API must show the public page as signed out, not a
    // blank screen. The mid-session 401 retry inside api() keeps waiting on the same refresh.
    if (!tokens.get() && !(await withTimeout(refreshSession(), 3000, false))) {
      set({ me: null, status: "anonymous" });
      return null;
    }
    try {
      const me = await api<Me>("/me");
      set({
        me,
        status: "authenticated",
        facilityId: pickFacility(me, get().facilityId),
      });
      return me;
    } catch {
      tokens.set(null);
      set({ me: null, status: "anonymous" });
      return null;
    }
  },
  async login(email, password, force = false) {
    const r = await api<{ accessToken: string }>("/auth/login", {
      method: "POST",
      json: { email, password, client: "web", force },
      anonymous: true,
    });
    tokens.set(r.accessToken);
    const me = await api<Me>("/me");
    set({
      me,
      status: "authenticated",
      facilityId: pickFacility(me, get().facilityId),
    });
    return me;
  },
  async logout() {
    await api("/auth/logout", { method: "POST", json: {} }).catch(
      () => undefined
    );
    tokens.set(null);
    set({ me: null, status: "anonymous" });
  },
  setFacility(id) {
    try {
      if (id === null) localStorage.removeItem(FACILITY_KEY);
      else localStorage.setItem(FACILITY_KEY, String(id));
    } catch {
      /* storage unavailable */
    }
    set({ facilityId: id });
  },
  can: (p) => get().me?.permissions.includes(p) ?? false,
  canViewForm: (formCode) => {
    const me = get().me;
    return !!me && (!me.forms || me.forms.includes(formCode));
  },
  canCreateForm: (formCode) => {
    const me = get().me;
    if (!me?.permissions.includes("document.create")) return false;
    // An API that predates formsCreate: the older rule (view grant stands in for create).
    if (me.formsCreate === undefined)
      return !me.forms || me.forms.includes(formCode);
    if (me.formsCreate === null) return !(formCode in FORM_ROLES);
    return me.formsCreate.includes(formCode);
  },
}));

/**
 * Keep a remembered facility only if the user may still see it; scoped users default to their first
 * plant.
 *
 * A head-office user has no `facilityIds` to check against, so this can only carry the remembered id
 * forward — whether that plant still EXISTS is settled by `useFacilities`, against the real list.
 */
function pickFacility(me: Me, remembered: number | null): number | null {
  if (me.facilityIds === null) return remembered;
  if (remembered !== null && me.facilityIds.includes(remembered))
    return remembered;
  return me.facilityIds[0] ?? null;
}

// A signed-in page whose session dies (signed out on another device, expired) goes to Login with a
// way back, instead of every request failing with 401 and the page sitting blank (user 2026-10-14).
// Only from "authenticated", so the start-up check and the Login page itself never loop.
// The reason (idle, signed in elsewhere) rides along so the Login page can say why.
onSessionLost.handler = (reason) => {
  if (useAuth.getState().status !== "authenticated") return;
  signOutTo(reason);
};

/** Drop the session locally and go to Login, coming back here afterwards. */
export function signOutTo(reason: SessionLostReason | null) {
  tokens.set(null);
  useAuth.setState({ me: null, status: "anonymous" });
  const back = window.location.pathname + window.location.search;
  const why = reason ? `&signedOut=${reason}` : "";
  window.location.assign(`/login?redirect=${encodeURIComponent(back)}${why}`);
}
